// src/belediye/pages/personel/PersonelDizinPage.js
// FAZ-18 · E1 — Personel Dizini (kapsam-türevli; mobil öncelikli).
// Liste + arama + birim filtresi. Kapsam backend'de; frontend yalnız gösterir.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { fetchPersonelDizin } from "../../services/personelApi";
import "./Personel.css";

const displayInitial = (p) => (p.ad || p.username || "?").slice(0, 1).toLocaleUpperCase("tr");

export default function PersonelDizinPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [unit, setUnit] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchPersonelDizin();
      setRows(Array.isArray(data) ? data : []);
    } catch (ex) {
      setError(
        ex?.response?.status === 403
          ? "Personel dizinini görüntüleme yetkiniz yok."
          : "Personel listesi yüklenemedi."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Birim filtresi seçenekleri = listedeki benzersiz birimler
  const birimler = useMemo(() => {
    const map = new Map();
    rows.forEach((p) => (p.birimler || []).forEach((b) => b?.id && map.set(b.id, b.ad)));
    return [...map.entries()].map(([id, ad]) => ({ id, ad }));
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr");
    return rows.filter((p) => {
      const hay = `${p.ad || ""} ${p.username || ""} ${p.phone_number || ""}`.toLocaleLowerCase("tr");
      const okQ = !needle || hay.includes(needle);
      const okUnit = !unit || (p.birimler || []).some((b) => String(b.id) === String(unit));
      return okQ && okUnit;
    });
  }, [rows, q, unit]);

  return (
    <div className="page-container pdz-root">
      <header className="pdz-head">
        <h1 className="pdz-title">Personel</h1>
        <p className="pdz-subtitle">Kapsamınızdaki personeli görüntüleyin.</p>
      </header>

      <div className="pdz-filters">
        <div className="pdz-search">
          <MagnifyingGlassIcon width={18} height={18} className="pdz-search-icon" />
          <input
            className="md-input pdz-search-input"
            placeholder="Ad, kullanıcı adı veya telefon ara"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Personel ara"
          />
        </div>
        {birimler.length > 1 && (
          <select
            className="md-select pdz-unit-filter"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            aria-label="Birime göre filtrele"
          >
            <option value="">Tüm birimler</option>
            {birimler.map((b) => (
              <option key={b.id} value={b.id}>
                {b.ad}
              </option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <div className="pdz-state">Yükleniyor…</div>
      ) : error ? (
        <div className="pdz-state pdz-state-error">
          {error}
          <button className="btn-md btn-md-ghost" onClick={load}>
            Tekrar dene
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="pdz-state">
          {rows.length === 0 ? "Kapsamınızda personel yok." : "Eşleşen personel bulunamadı."}
        </div>
      ) : (
        <ul className="pdz-list">
          {filtered.map((p) => (
            <li key={p.id}>
              <button
                className="md-card pdz-item"
                onClick={() => navigate(`/yonetim/personel/${p.id}`)}
              >
                <span className="pdz-item-main">
                  <span className="pdz-avatar" aria-hidden="true">
                    {displayInitial(p)}
                  </span>
                  <span className="pdz-item-info">
                    <span className="pdz-item-name">{p.ad}</span>
                    <span className="pdz-item-username">@{p.username}</span>
                  </span>
                </span>
                <span className="pdz-item-meta">
                  {p.is_superuser && <span className="md-chip md-chip-danger">Süper kullanıcı</span>}
                  {p.rol && <span className="md-chip">{p.rol}</span>}
                  <span className="pdz-item-units">{p.birim_sayisi || 0} birim</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
